
import axios from 'axios';
import { BedStatus } from '../types/enums';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

// =============================================================================
// BED CRUD OPERATIONS
// =============================================================================

// Add a new bed and corresponding bedStatus
export async function addBed({ facilityId, departmentId, roomId, name, code, type, status = BedStatus.AVAILABLE, ...rest }) {
  try {
    console.log('Adding bed via API:', code);
    const token = await getAuthToken();
    
    const bedData = {
      facilityId,
      departmentId,
      roomId,
      name,
      code,
      type,
      status,
      ...rest
    };
    
    const response = await axios.post(
      `${API_BASE}/internal/beds`,
      bedData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return { 
      ...bedData, 
      id: response.data.id,
      docId: response.data.id
    };
  } catch (error) {
    console.error('Error adding bed:', error);
    throw error;
  }
}

// Update a bed by bed UUID
export async function updateBed(bedId, updates) {
  try {
    console.log('Updating bed via API:', bedId);
    const token = await getAuthToken();
    
    await axios.put(
      `${API_BASE}/internal/beds/${bedId}`,
      updates,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return true;
  } catch (error) {
    console.error('Error updating bed:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Bed not found');
    }
    throw error;
  }
}

// Delete a bed by bed UUID
export async function deleteBed(bedId) {
  try {
    console.log('Deleting bed via API:', bedId);
    const token = await getAuthToken();
    
    await axios.delete(
      `${API_BASE}/internal/beds/${bedId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return true;
  } catch (error) {
    console.error('Error deleting bed:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Bed not found');
    }
    throw error;
  }
}

// Get all beds for a room
export async function getBedsByRoom(roomId, facilityId) {
  try {
    console.log('Getting beds by room via API:', roomId, 'facilityId:', facilityId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('roomId', roomId);
    params.append('facilityId', facilityId);
    
    const response = await axios.get(
      `${API_BASE}/internal/beds?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.beds.map(bed => ({ ...bed, docId: bed.id }));
  } catch (error) {
    console.error('Error getting beds by room:', error);
    throw error;
  }
}

// Get all beds for a facility
export async function getBedsByFacility(facilityId) {
  try {
    console.log('Getting beds by facility via API:', facilityId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    
    const response = await axios.get(
      `${API_BASE}/internal/beds?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.beds.map(bed => ({ ...bed, docId: bed.id }));
  } catch (error) {
    console.error('Error getting beds by facility:', error);
    throw error;
  }
}

// Get a bed by UUID
export async function getBedById(bedId) {
  try {
    console.log('Getting bed by ID via API:', bedId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/internal/beds/${bedId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    const bed = response.data.bed;
    return { ...bed, docId: bed.id };
  } catch (error) {
    console.error('Error getting bed by ID:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// =============================================================================
// BED STATUS CRUD OPERATIONS
// =============================================================================

/**
 * Get bed status by bedId
 */
export async function getBedStatus(bedId) {
  try {
    console.log('Getting bed status via API:', bedId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/internal/beds/status/${bedId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.bedStatus;
  } catch (error) {
    console.error('Error getting bed status:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Update bed status
 */
export async function updateBedStatus(bedId, updates) {
  try {
    console.log('Updating bed status via API:', bedId);
    const token = await getAuthToken();
    
    await axios.put(
      `${API_BASE}/internal/beds/status/${bedId}`,
      updates,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return true;
  } catch (error) {
    console.error('Error updating bed status:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Bed status not found');
    }
    throw error;
  }
}

/**
 * Get available beds for a facility with optional filters
 */
export async function getAvailableBeds(facilityId, filters = {}) {
  try {
    console.log('Getting available beds via API:', facilityId, filters);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/internal/beds/available?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.availableBeds;
  } catch (error) {
    console.error('Error getting available beds:', error);
    throw error;
  }
}

/**
 * Create a new bed status document
 */
export async function createBedStatus({ 
  facilityId, 
  departmentId, 
  roomId, 
  bedId, 
  status, 
  type, 
  name,
  capacity = 1,
  genderRestriction = 'none',
  isolationCapable = false,
  icuCapable = false,
  bariatricCapable = false
}) {
  try {
    console.log('Creating bed status via API:', bedId);
    const token = await getAuthToken();
    
    const bedStatusData = {
      id: bedId,
      facilityId,
      departmentId,
      roomId,
      bedId,
      status: status || BedStatus.AVAILABLE,
      capacity,
      genderRestriction,
      isolationCapable,
      icuCapable,
      bariatricCapable
    };

    // Add optional fields if provided
    if (name) bedStatusData.name = name;
    if (type) bedStatusData.type = type;

    await axios.post(
      `${API_BASE}/internal/beds/status`,
      bedStatusData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return true;
  } catch (error) {
    console.error('Error creating bed status:', error);
    throw error;
  }
}

/**
 * Initialize bed status for all beds in a facility
 */
export async function initializeBedStatus(facilityId, departments) {
  try {
    console.log('Initializing bed status via API:', facilityId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/internal/beds/initialize-status`,
      {
        facilityId,
        departments
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Bed status initialized successfully:', response.data.totalBeds);
    return true;
  } catch (error) {
    console.error('Error initializing bed status:', error);
    throw error;
  }
}

// =============================================================================
// ADDITIONAL BED MANAGEMENT FUNCTIONS
// =============================================================================

/**
 * Get bed status for multiple beds
 */
export async function getMultipleBedStatus(bedIds) {
  try {
    console.log('Getting multiple bed status via API:', bedIds.length);
    const promises = bedIds.map(bedId => getBedStatus(bedId));
    const results = await Promise.allSettled(promises);
    
    return results.map((result, index) => ({
      bedId: bedIds[index],
      status: result.status === 'fulfilled' ? result.value : null,
      error: result.status === 'rejected' ? result.reason : null
    }));
  } catch (error) {
    console.error('Error getting multiple bed status:', error);
    throw error;
  }
}

/**
 * Get bed occupancy summary for a facility
 */
export async function getBedOccupancySummary(facilityId) {
  try {
    console.log('Getting bed occupancy summary via API:', facilityId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/internal/beds/occupancy-summary?facilityId=${facilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.summary;
  } catch (error) {
    // If endpoint doesn't exist, calculate manually
    try {
      const allBeds = await getBedsByFacility(facilityId);
      const availableBeds = await getAvailableBeds(facilityId);
      
      return {
        totalBeds: allBeds.length,
        availableBeds: availableBeds.length,
        occupiedBeds: allBeds.length - availableBeds.length,
        occupancyRate: allBeds.length > 0 ? ((allBeds.length - availableBeds.length) / allBeds.length * 100).toFixed(1) : 0
      };
    } catch (fallbackError) {
      console.error('Error getting bed occupancy summary (fallback):', fallbackError);
      throw error;
    }
  }
}

/**
 * Search beds by criteria
 */
export async function searchBeds(searchCriteria) {
  try {
    console.log('Searching beds via API:', searchCriteria);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    Object.entries(searchCriteria).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/internal/beds/search?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.beds;
  } catch (error) {
    // If search endpoint doesn't exist, use available beds endpoint
    try {
      if (searchCriteria.facilityId) {
        return await getAvailableBeds(searchCriteria.facilityId, searchCriteria);
      }
      throw new Error('Facility ID is required for bed search');
    } catch (fallbackError) {
      console.error('Error searching beds (fallback):', fallbackError);
      throw error;
    }
  }
}

/**
 * Get beds by department
 */
export async function getBedsByDepartment(facilityId, departmentId) {
  try {
    const searchCriteria = {
      facilityId,
      departmentId
    };
    return await searchBeds(searchCriteria);
  } catch (error) {
    console.error('Error getting beds by department:', error);
    throw error;
  }
}

/**
 * Get beds by type
 */
export async function getBedsByType(facilityId, bedType) {
  try {
    const allBeds = await getBedsByFacility(facilityId);
    return allBeds.filter(bed => bed.type === bedType);
  } catch (error) {
    console.error('Error getting beds by type:', error);
    throw error;
  }
}

/**
 * Check bed availability
 */
export async function isBedAvailable(bedId) {
  try {
    const bedStatus = await getBedStatus(bedId);
    return bedStatus && bedStatus.status === BedStatus.AVAILABLE;
  } catch (error) {
    console.error('Error checking bed availability:', error);
    return false;
  }
}

/**
 * Bulk update bed status
 */
export async function bulkUpdateBedStatus(bedUpdates) {
  try {
    console.log('Bulk updating bed status via API:', bedUpdates.length);
    const promises = bedUpdates.map(({ bedId, updates }) => 
      updateBedStatus(bedId, updates)
    );
    
    const results = await Promise.allSettled(promises);
    
    const summary = {
      successful: 0,
      failed: 0,
      errors: []
    };
    
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        summary.successful++;
      } else {
        summary.failed++;
        summary.errors.push({
          bedId: bedUpdates[index].bedId,
          error: result.reason
        });
      }
    });
    
    return summary;
  } catch (error) {
    console.error('Error bulk updating bed status:', error);
    throw error;
  }
}

/**
 * Get bed utilization statistics
 */
export async function getBedUtilizationStats(facilityId, dateRange = {}) {
  try {
    console.log('Getting bed utilization stats via API:', facilityId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    
    if (dateRange.startDate) {
      params.append('startDate', dateRange.startDate);
    }
    if (dateRange.endDate) {
      params.append('endDate', dateRange.endDate);
    }
    
    const response = await axios.get(
      `${API_BASE}/internal/beds/utilization-stats?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.stats;
  } catch (error) {
    // If endpoint doesn't exist, return basic summary
    try {
      const summary = await getBedOccupancySummary(facilityId);
      return {
        occupancyRate: summary.occupancyRate,
        totalBeds: summary.totalBeds,
        averageOccupancy: summary.occupancyRate,
        peakOccupancy: summary.occupancyRate,
        period: dateRange
      };
    } catch (fallbackError) {
      console.error('Error getting bed utilization stats (fallback):', fallbackError);
      throw error;
    }
  }
}
